import React, { useEffect, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { WhitelabelProvider } from './contexts/WhitelabelContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/global.css';
import './styles/components-override.css';

import useAuth from './hooks/useAuth';
import { hasPermission } from './config/permissions';
import { isSubdomain, isRootDomain, setClusterHostnames } from './utils/subdomain';
import { SERVER_URL } from './config';


import { AuthSkeleton, LandingPageSkeleton, GenericSkeleton } from './components/common/SkeletonLoaders';

// Layouts (lazy — landing page never loads these)
const DashboardLayout = React.lazy(() => import('./components/layout/DashboardLayout'));
const AdminLayout = React.lazy(() => import('./components/layout/AdminLayout'));

// Lazy-loaded pages
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const MessagesPage = React.lazy(() => import('./pages/MessagesPage'));
const CampaignsPage = React.lazy(() => import('./pages/CampaignsPage'));
const CampaignDetailPage = React.lazy(() => import('./pages/CampaignDetailPage'));
const SubscriptionsPage = React.lazy(() => import('./pages/SubscriptionsPage'));
const ContactsPage = React.lazy(() => import('./pages/ContactsPage'));
const TemplatesPage = React.lazy(() => import('./pages/TemplatesPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const ApiSettingsPage = React.lazy(() => import('./pages/ApiSettingsPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const BillingPage = React.lazy(() => import('./pages/BillingPage'));
const WhitelabelPage = React.lazy(() => import('./pages/WhitelabelPage'));
const WhatsAppSetupPage = React.lazy(() => import('./pages/WhatsAppSetupPage'));
const WhatsAppSendPage = React.lazy(() => import('./pages/WhatsAppSendPage'));
const WhatsAppGroupsPage = React.lazy(() => import('./pages/WhatsAppGroupsPage'));
const WhatsAppToolsPage = React.lazy(() => import('./pages/WhatsAppToolsPage'));
const WaContactsPage = React.lazy(() => import('./pages/WaContactsPage'));
const WaOnlinePage = React.lazy(() => import('./pages/WaOnlinePage'));
const WhatsAppStatusPage = React.lazy(() => import('./pages/WhatsAppStatusPage'));
const WhatsAppStatusReplyPage = React.lazy(() => import('./pages/WhatsAppStatusReplyPage'));
const BusinessApiGuidePage = React.lazy(() => import('./pages/BusinessApiGuidePage'));
const BusinessApiConfigPage = React.lazy(() => import('./pages/BusinessApiConfigPage'));
const WaTemplatesPage = React.lazy(() => import('./pages/WaTemplatesPage'));
const WebhookTesterPage = React.lazy(() => import('./pages/WebhookTesterPage'));
const ChatbotFlowPage = React.lazy(() => import('./pages/ChatbotFlowPage'));
const GroupChatbotPage = React.lazy(() => import('./pages/GroupChatbotPage'));
const ActivityLogsPage = React.lazy(() => import('./pages/ActivityLogsPage'));
const UserManagementPage = React.lazy(() => import('./pages/UserManagementPage'));
const ApiExplorerPage = React.lazy(() => import('./pages/ApiExplorerPage'));
const IntegrationGuidePage = React.lazy(() => import('./pages/IntegrationGuidePage'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const LegalPage = React.lazy(() => import('./pages/LegalPage'));
const BlogIndexPage = React.lazy(() => import('./pages/BlogIndexPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));

// Business API pages (lazy)
const EmbeddedSignupPage = React.lazy(() => import('./pages/EmbeddedSignupPage'));
const BusinessTemplatesPage = React.lazy(() => import('./pages/BusinessTemplatesPage'));
const InboxPage = React.lazy(() => import('./pages/InboxPage'));
const BusinessCampaignsPage = React.lazy(() => import('./pages/BusinessCampaignsPage'));
const BusinessCampaignDetailPage = React.lazy(() => import('./pages/BusinessCampaignDetailPage'));
const BusinessAnalyticsPage = React.lazy(() => import('./pages/BusinessAnalyticsPage'));
const PhoneManagementPage = React.lazy(() => import('./pages/PhoneManagementPage'));
const CatalogPage = React.lazy(() => import('./pages/CatalogPage'));
const MediaLibraryPage = React.lazy(() => import('./pages/MediaLibraryPage'));
const WhatsAppFlowsPage = React.lazy(() => import('./pages/WhatsAppFlowsPage'));
const ComingSoonPage = React.lazy(() => import('./pages/ComingSoonPage'));
const BusinessCallsPage = React.lazy(() => import('./pages/BusinessCallsPage'));
const MarketingLitePage = React.lazy(() => import('./pages/MarketingLitePage'));
const ChatWidgetPage = React.lazy(() => import('./pages/ChatWidgetPage'));
const ChatWidgetTestPage = React.lazy(() => import('./pages/ChatWidgetTestPage'));
const ProxyIpsPage = React.lazy(() => import('./pages/ProxyIpsPage'));
const CannedResponsesPage = React.lazy(() => import('./pages/CannedResponsesPage'));
const SLAPoliciesPage = React.lazy(() => import('./pages/SLAPoliciesPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const BookingsPage = React.lazy(() => import('./pages/BookingsPage'));
const PaymentsPage = React.lazy(() => import('./pages/PaymentsPage'));
const CTWAAdsPage = React.lazy(() => import('./pages/CTWAAdsPage'));
const HealthScorePage = React.lazy(() => import('./pages/HealthScorePage'));
const IndustryPacksPage = React.lazy(() => import('./pages/IndustryPacksPage'));

// Admin pages (lazy)
const AdminDashboardPage = React.lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminTenantsPage = React.lazy(() => import('./pages/admin/AdminTenantsPage'));
const AdminClustersPage = React.lazy(() => import('./pages/admin/AdminClustersPage'));
const AdminModelPage = React.lazy(() => import('./pages/admin/AdminModelPage'));
const AdminEmailTemplatesPage = React.lazy(() => import('./pages/admin/AdminEmailTemplatesPage'));
const AdminStatusPage = React.lazy(() => import('./pages/admin/AdminStatusPage'));
const PublicStatusPage = React.lazy(() => import('./pages/PublicStatusPage'));

const PageFallback = () => (
  <div style={{ minHeight: '60vh', padding: '2rem' }}>
    <div className="placeholder-glow">
      <span className="placeholder col-4 d-block mb-3" style={{ height: 28, borderRadius: 4 }} />
      <span className="placeholder col-6 d-block mb-4" style={{ height: 14, borderRadius: 4 }} />
      <div className="card" style={{ border: '1px solid #e9ecef', borderRadius: 8 }}>
        <div className="card-body">
          <span className="placeholder col-12 d-block mb-3" style={{ height: 16, borderRadius: 4 }} />
          <span className="placeholder col-10 d-block mb-3" style={{ height: 16, borderRadius: 4 }} />
          <span className="placeholder col-8 d-block mb-3" style={{ height: 16, borderRadius: 4 }} />
          <span className="placeholder col-11 d-block" style={{ height: 16, borderRadius: 4 }} />
        </div>
      </div>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageFallback />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PlatformAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageFallback />;
  }

  if (!user || user.role !== 'platform_admin') {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const RoleRoute = ({ permission, children }) => {
  const { user } = useAuth();
  if (!user || !hasPermission(user.role, permission)) {
    return <Navigate to="/dashboard" replace />;
  }
  // Wrap permission-gated children in an ErrorBoundary so a render error in
  // any lazy-loaded permission-gated page (Inbox, Campaigns, CTWA, etc.)
  // surfaces a fallback instead of bubbling to the SPA root and blanking the
  // entire app. The boundary is per-route, so other routes stay interactive.
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <AuthSkeleton />;
  }

  if (user) {
    // Platform admin → admin panel; tenant user on subdomain → dashboard; root → landing
    if (user.role === 'platform_admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to={isSubdomain() ? '/dashboard' : '/'} replace />;
  }

  return children;
};

const AppRoutes = () => {
  const onSubdomain = isSubdomain();
  const onRoot = isRootDomain();

  return (
    <Routes>
      {/* Public Routes — Suspense here only covers the public page, not layouts */}
      <Route path="/login" element={<PublicRoute><Suspense fallback={<AuthSkeleton />}><LoginPage /></Suspense></PublicRoute>} />
      <Route path="/register" element={onRoot ? <PublicRoute><Suspense fallback={<AuthSkeleton />}><RegisterPage /></Suspense></PublicRoute> : <Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={<PublicRoute><Suspense fallback={<AuthSkeleton />}><ForgotPasswordPage /></Suspense></PublicRoute>} />

      {/* Platform Admin Routes — only on root domain */}
      <Route path="/admin" element={<PlatformAdminRoute><Suspense fallback={<GenericSkeleton />}><AdminLayout /></Suspense></PlatformAdminRoute>}>
        <Route index element={<AdminDashboardPage />} />
        <Route path="tenants" element={<AdminTenantsPage />} />
        <Route path="clusters" element={<AdminClustersPage />} />
        <Route path="email-templates" element={<AdminEmailTemplatesPage />} />
        <Route path="status" element={<AdminStatusPage />} />
        <Route path="proxy-ips" element={<ProxyIpsPage />} />
        <Route path="db/:model" element={<AdminModelPage />} />
      </Route>

      {/* Protected Dashboard Routes — only on subdomain */}
      <Route path="/dashboard" element={onRoot ? <Navigate to="/" replace /> : <ProtectedRoute><Suspense fallback={<GenericSkeleton />}><DashboardLayout /></Suspense></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="business-api-guide" element={<RoleRoute permission="business_api.guide"><BusinessApiGuidePage /></RoleRoute>} />
        <Route path="business-api-config" element={<RoleRoute permission="business_api.config"><BusinessApiConfigPage /></RoleRoute>} />
        <Route path="embedded-signup" element={<RoleRoute permission="business_api.config"><EmbeddedSignupPage /></RoleRoute>} />
        <Route path="inbox" element={<RoleRoute permission="business_api.inbox"><InboxPage /></RoleRoute>} />
        <Route path="business-templates" element={<RoleRoute permission="business_api.templates"><BusinessTemplatesPage /></RoleRoute>} />
        <Route path="business-campaigns" element={<RoleRoute permission="business_api.campaigns"><BusinessCampaignsPage /></RoleRoute>} />
        <Route path="business-campaigns/:uuid" element={<RoleRoute permission="business_api.campaigns"><BusinessCampaignDetailPage /></RoleRoute>} />
        <Route path="business-analytics" element={<RoleRoute permission="business_api.analytics"><BusinessAnalyticsPage /></RoleRoute>} />
        <Route path="phone-management" element={<RoleRoute permission="business_api.phone_mgmt"><PhoneManagementPage /></RoleRoute>} />
        <Route path="catalog" element={<RoleRoute permission="business_api.catalog"><CatalogPage /></RoleRoute>} />
        <Route path="media-library" element={<RoleRoute permission="business_api.media"><MediaLibraryPage /></RoleRoute>} />
        <Route path="wa-flows" element={<RoleRoute permission="business_api.flows"><WhatsAppFlowsPage /></RoleRoute>} />
        <Route path="business-calls" element={<RoleRoute permission="business_api.calls"><BusinessCallsPage /></RoleRoute>} />
        <Route path="business-payments" element={<RoleRoute permission="business_api.config"><ComingSoonPage title="In-Chat Payments" description="Collect payments directly within WhatsApp conversations. Support for UPI, cards, and other payment methods." icon={null} expectedDate="Q4 2026" /></RoleRoute>} />
        <Route path="business-mm-lite" element={<RoleRoute permission="business_api.mm_lite"><MarketingLitePage /></RoleRoute>} />
        <Route path="business-groups" element={<RoleRoute permission="business_api.send"><ComingSoonPage title="Cloud Groups" description="Create and manage WhatsApp groups directly via the Cloud API." icon={null} expectedDate="Q4 2026" /></RoleRoute>} />
        <Route path="send" element={<RoleRoute permission="messages.send"><MessagesPage /></RoleRoute>} />
        <Route path="campaigns" element={<RoleRoute permission="campaigns.manage"><CampaignsPage /></RoleRoute>} />
        <Route path="campaigns/:uuid" element={<RoleRoute permission="campaigns.manage"><CampaignDetailPage /></RoleRoute>} />
        <Route path="subscriptions" element={<RoleRoute permission="subscriptions.manage"><SubscriptionsPage /></RoleRoute>} />
        <Route path="contacts" element={<RoleRoute permission="contacts.manage"><ContactsPage /></RoleRoute>} />
        <Route path="groups" element={<RoleRoute permission="contacts.manage"><ContactsPage /></RoleRoute>} />
        <Route path="templates" element={<RoleRoute permission="templates.manage"><TemplatesPage /></RoleRoute>} />
        <Route path="wa-templates" element={<RoleRoute permission="wa.templates"><WaTemplatesPage /></RoleRoute>} />
        <Route path="reports" element={<RoleRoute permission="reports.view"><ReportsPage /></RoleRoute>} />
        <Route path="api-settings" element={<RoleRoute permission="api_settings.manage"><ApiSettingsPage /></RoleRoute>} />
        <Route path="webhooks" element={<RoleRoute permission="webhooks.manage"><ApiSettingsPage /></RoleRoute>} />
        <Route path="profile" element={<ProfilePage />} />
        {/* billing.view lets tenant admins see balance/transactions/plans; */}
        {/* the recharge buttons inside BillingPage gate on billing.manage. */}
        <Route path="billing" element={<RoleRoute permission="billing.view"><BillingPage /></RoleRoute>} />
        <Route path="whitelabel" element={<RoleRoute permission="whitelabel.manage"><WhitelabelPage /></RoleRoute>} />
        <Route path="wa-setup" element={<RoleRoute permission="wa.setup"><WhatsAppSetupPage /></RoleRoute>} />
        <Route path="wa-send" element={<RoleRoute permission="wa.send"><WhatsAppSendPage /></RoleRoute>} />
        <Route path="wa-groups" element={<RoleRoute permission="wa.groups"><WhatsAppGroupsPage /></RoleRoute>} />
        <Route path="wa-status" element={<RoleRoute permission="wa.status"><WhatsAppStatusPage /></RoleRoute>} />
        <Route path="wa-status-reply" element={<RoleRoute permission="wa.status_reply.view"><WhatsAppStatusReplyPage /></RoleRoute>} />
        <Route path="wa-contacts" element={<RoleRoute permission="wa.contacts"><WaContactsPage /></RoleRoute>} />
        <Route path="wa-online" element={<RoleRoute permission="wa.online"><WaOnlinePage /></RoleRoute>} />
        <Route path="wa-tools" element={<RoleRoute permission="wa.tools"><WhatsAppToolsPage /></RoleRoute>} />
        <Route path="wa-chatbot" element={<RoleRoute permission="wa.chatbot"><ChatbotFlowPage /></RoleRoute>} />
        <Route path="wa-group-chatbot" element={<RoleRoute permission="wa.chatbot"><GroupChatbotPage /></RoleRoute>} />
        <Route path="webhook-tester" element={<RoleRoute permission="webhook_tester.manage"><WebhookTesterPage /></RoleRoute>} />
        <Route path="api-explorer" element={<RoleRoute permission="api_explorer.view"><ApiExplorerPage /></RoleRoute>} />
        <Route path="integration-guide" element={<RoleRoute permission="integration_guide.view"><IntegrationGuidePage /></RoleRoute>} />
        <Route path="activity-logs" element={<RoleRoute permission="activity_logs.view"><ActivityLogsPage /></RoleRoute>} />
        <Route path="users" element={<RoleRoute permission="users.manage"><UserManagementPage /></RoleRoute>} />
        <Route path="chat-widget" element={<RoleRoute permission="chat_widget.view"><ChatWidgetPage /></RoleRoute>} />
        <Route path="chat-widget/test" element={<RoleRoute permission="chat_widget.view"><ChatWidgetTestPage /></RoleRoute>} />
        <Route path="proxy-ips" element={<RoleRoute permission="proxy_ips.view"><ProxyIpsPage /></RoleRoute>} />
        <Route path="canned-responses" element={<RoleRoute permission="canned_responses.use"><CannedResponsesPage /></RoleRoute>} />
        <Route path="sla" element={<RoleRoute permission="sla.view"><SLAPoliciesPage /></RoleRoute>} />
        <Route path="privacy" element={<RoleRoute permission="privacy.export"><PrivacyPage /></RoleRoute>} />
        <Route path="bookings" element={<RoleRoute permission="bookings.view"><BookingsPage /></RoleRoute>} />
        <Route path="payments" element={<RoleRoute permission="payments.view"><PaymentsPage /></RoleRoute>} />
        <Route path="ctwa-ads" element={<RoleRoute permission="ctwa_ads.view"><CTWAAdsPage /></RoleRoute>} />
        <Route path="health-score" element={<RoleRoute permission="health_score.view"><HealthScorePage /></RoleRoute>} />
        <Route path="industry-packs" element={<RoleRoute permission="industry_packs.view"><IndustryPacksPage /></RoleRoute>} />
      </Route>

      {/* Default: Landing page on root domain, login on subdomain */}
      <Route path="/" element={onSubdomain ? <Navigate to="/login" replace /> : <Suspense fallback={<LandingPageSkeleton />}><LandingPage /></Suspense>} />
      {/* Brand-name vanity URLs — same LandingPage, brand-anchored meta via seoPathMeta */}
      <Route path="/wapingohub" element={onSubdomain ? <Navigate to="/login" replace /> : <Suspense fallback={<LandingPageSkeleton />}><LandingPage /></Suspense>} />
      <Route path="/wa-pingohub" element={onSubdomain ? <Navigate to="/login" replace /> : <Suspense fallback={<LandingPageSkeleton />}><LandingPage /></Suspense>} />
      <Route path="/pingohub" element={onSubdomain ? <Navigate to="/login" replace /> : <Suspense fallback={<LandingPageSkeleton />}><LandingPage /></Suspense>} />
      {/* Section pages — separate URLs for Google sitelinks. LandingPage auto-scrolls to mapped section. */}
      <Route path="/pricing" element={onSubdomain ? <Navigate to="/login" replace /> : <Suspense fallback={<LandingPageSkeleton />}><LandingPage /></Suspense>} />
      <Route path="/features" element={onSubdomain ? <Navigate to="/login" replace /> : <Suspense fallback={<LandingPageSkeleton />}><LandingPage /></Suspense>} />
      <Route path="/about" element={onSubdomain ? <Navigate to="/login" replace /> : <Suspense fallback={<LandingPageSkeleton />}><LandingPage /></Suspense>} />
      <Route path="/contact" element={onSubdomain ? <Navigate to="/login" replace /> : <Suspense fallback={<LandingPageSkeleton />}><LandingPage /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<LandingPageSkeleton />}><LegalPage slug="privacy" /></Suspense>} />
      <Route path="/terms" element={<Suspense fallback={<LandingPageSkeleton />}><LegalPage slug="terms" /></Suspense>} />
      <Route path="/refund-policy" element={<Suspense fallback={<LandingPageSkeleton />}><LegalPage slug="refund-policy" /></Suspense>} />
      <Route path="/cookie-policy" element={<Suspense fallback={<LandingPageSkeleton />}><LegalPage slug="cookie-policy" /></Suspense>} />
      <Route path="/acceptable-use" element={<Suspense fallback={<LandingPageSkeleton />}><LegalPage slug="acceptable-use" /></Suspense>} />
      <Route path="/blog" element={<Suspense fallback={<LandingPageSkeleton />}><BlogIndexPage /></Suspense>} />
      <Route path="/blog/:slug" element={<Suspense fallback={<LandingPageSkeleton />}><BlogPostPage /></Suspense>} />
      <Route path="/status" element={<Suspense fallback={<LandingPageSkeleton />}><PublicStatusPage /></Suspense>} />
      <Route path="*" element={<Navigate to={onSubdomain ? '/login' : '/'} replace />} />
    </Routes>
  );
};

function App() {
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    // Skip cluster fetch on localhost AND on the bare root domain — neither needs it.
    // The cluster cache only matters when extracting tenant from a custom domain
    // subdomain (e.g. `acme.someotherhost.com`). The bare landing page never reads
    // it, so we save one blocking API call on first paint.
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
    const isBareRoot = host === 'wapingohub.in' || host === 'www.wapingohub.in';
    if (isLocal || isBareRoot) return;

    fetch(`${SERVER_URL}api/v1/cluster/config`)
      .then(res => res.ok ? res.json() : null)
      .then(json => {
        if (json?.success && json.data?.hostnames) {
          setClusterHostnames(json.data.hostnames, json.data.tenantBaseDomains);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <Router>
      <AuthProvider>
        <WhitelabelProvider>
        <ConfirmProvider>
        <AppRoutes />
        </ConfirmProvider>
        </WhitelabelProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
